import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import { appendPracticeToOrganization } from 'api/Practice';

import { usePractice } from 'lib/hooks/queries/practice/usePractice';
import { useInfinitePracticeOwners } from 'lib/hooks/queries/practice/usePracticeOwners';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Input from 'components/shared/Input/Input';
import Spinner from 'components/shared/spinner/spinner';

import CreateOrganization from './components/CreateOrganization/CreateOrganizationModal';
import OrganizationCard from './components/OrganizationCard';
import OrganizationConfirmationModal from './components/OrganizationConfirmationModal';
import OrganizationDetails from './components/OrganizationDetails';

const AdminOrganizations = ({
  addFooterActions,
  setHeaderButtonProps,
  isAdmin,
  onNext,
  mutateOrchestratorData,
  orchestratorLoading
}) => {
  const { practice } = useOutletContext();
  const { id: practiceId } = useParams();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const { data } = usePractice({ practice_id: practice?.organization?.id });

  const limit = 25;

  const {
    data: organizationsData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useInfinitePracticeOwners({
    params: { withCount: true, practiceId, searchTerm: debouncedSearchTerm, limit, page: 1 },
    dependencies: [debouncedSearchTerm]
  });

  const scrollRef = useRef(null);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollTop + clientHeight >= scrollHeight - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const el = document.querySelector('main.stepper-main-content');
    scrollRef.current = el;

    if (!el) return;

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const { mutate: appendPracticeToOrganizationMutate } = useCustomMutation({
    mutationFn: (data) => appendPracticeToOrganization(navigate, data),
    invalidateQueryKey: ['practice'],
    successMessage: 'Practice added to organization successfully!'
  });

  const handleSubmit = (selectedOrganization) => {
    appendPracticeToOrganizationMutate({
      importAll: false,
      ownerPracticeId: selectedOrganization?.id,
      practiceId: Number(practiceId),
      selectedLocation: selectedOrganization?.id,
      userId: selectedOrganization?.owners?.[0]?.user?.id,
      selectedOrganization
    });
  };

  useEffect(() => {
    if (!practice?.organization?.id && isAdmin) {
      setHeaderButtonProps({
        text: 'New organization',
        onClick: () => setOpenModal(true)
      });
    } else {
      setHeaderButtonProps(null);
    }
    addFooterActions([
      {
        text: 'Continue',
        onClick: () => {
          if (!isAdmin) {
            return onNext();
          }
          if (!practice?.organization?.id) {
            setShowConfirmation(true);
          } else {
            mutateOrchestratorData({ organizationChoice: 'add' });
          }
        },
        isDisabled: orchestratorLoading
      }
    ]);
  }, [practice?.organization?.id, orchestratorLoading, isAdmin]);

  if (practice?.organization?.id || !isAdmin) {
    return (
      <OrganizationDetails
        isAdmin={isAdmin}
        practice={practice}
        organization={data?.practices}
        members={data?.members?.members}
      />
    );
  }

  return (
    <div className="flex w-full justify-center">
      <CreateOrganization isOpen={openModal} handleClose={() => setOpenModal(false)} />
      <OrganizationConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onMarkComplete={() => {
          setShowConfirmation(false);
          mutateOrchestratorData({ organizationChoice: 'skip' });
        }}
        onSkip={() => {
          setShowConfirmation(false);
          mutateOrchestratorData({ organizationChoice: 'skip' });
        }}
        onAddNewOrganization={() => {
          setShowConfirmation(false);
          setOpenModal(true);
        }}
        isLoading={orchestratorLoading}
      />
      <div className="mb-6 w-full text-left">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Input
              inputWrapperClassName="!bg-transparent !h-10"
              className="flex-1"
              icon="search-linear"
              placeholder="Search by organization name, ID, address or owner"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {isLoading ? (
            <div className="flex h-full w-full items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <OrganizationCard
              organizationsData={organizationsData}
              handleSubmit={handleSubmit}
              practiceName={practice?.name}
              isFetchingNextPage={isFetchingNextPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminOrganizations;
